import { EsqueletoDaCasca, formaDaRota } from '../ui/esqueletos';
import { useEffect, useRef, type ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth, useCan, type AuthStatus } from './auth';
import type { AppCode, Permission } from './permissions';
import { clienteDoHost } from './dominios';
import { comSlug, useSlugDoCliente } from './rotas';

/** Máquina de desenvolvimento: nenhum desvio para o domínio público acontece aqui. */
const ehHostLocal = () => {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h.endsWith('.localhost');
};

const slugDoSubdominio = () => (typeof window !== 'undefined' ? clienteDoHost(window.location.host) : null);

type Props = {
  app?: AppCode;
  permission?: Permission;
  /** Bastidor da Yard: nem o dono da organização entra, só a equipe. */
  soYard?: boolean;
  children: ReactNode;
};

/**
 * V26/V43: as telas de conta que só olham `auth.uid()`, nunca a organização.
 *
 * O administrador da Yard sem organização própria era mandado para `/admin`
 * em TODA rota fora do console - inclusive no próprio Perfil e em Segurança,
 * que é onde se troca senha e se liga MFA. A conta com mais poder era a
 * única sem tela para se proteger. Estas duas passam sem organização; o
 * resto de Configurações (usuários, marca, organização, plano...) é da
 * organização e continua precisando de uma.
 */
export const ROTAS_SEM_TENANT = ['/settings/profile', '/settings/security'] as const;

/**
 * O caminho sem o slug do cliente na frente, quando ele está lá
 * (`/cima/settings/profile` -> `/settings/profile`). Em subdomínio o slug
 * não está no caminho, então não há o que tirar.
 */
export function rotaSemTenant(pathname: string, slugDoCaminho?: string | null): boolean {
  const caminho =
    slugDoCaminho && pathname.startsWith(`/${slugDoCaminho}/`)
      ? pathname.slice(slugDoCaminho.length + 1)
      : pathname;
  return ROTAS_SEM_TENANT.some((r) => caminho === r || caminho.startsWith(`${r}/`));
}

/** O que a guarda enxerga - só o que a decisão precisa, sem React. */
export type EntradaDaGuarda = {
  status: AuthStatus;
  adminPlataforma: boolean;
  /** Slug da organização ativa da sessão; nulo para a equipe sem organização. */
  orgSlug: string | null;
  /** Slugs das organizações em que a pessoa tem vínculo. */
  slugsDasOrgs: string[];
  /** O slug que está na barra de endereço - caminho ou subdomínio. */
  slugDaUrl?: string | undefined;
  /** O slug veio do CAMINHO (`/cima/home`), não do subdomínio. */
  slugDoCaminho?: string | null | undefined;
  /** Estamos em `{slug}.yardsuite.com.br`. */
  emSubdominio: boolean;
  /** Para prefixar destino (`comSlug`) - nulo em subdomínio. */
  slugAtivo: string | null;
  pathname: string;
  search: string;
  installedApps: AppCode[];
  app?: AppCode | undefined;
  permission?: Permission | undefined;
  /** O que `useCan(permission)` respondeu. */
  can: boolean;
  soYard?: boolean | undefined;
};

export type Desvio =
  | { tipo: 'carregando' }
  | { tipo: 'erro' }
  | { tipo: 'trocando' }
  | { tipo: 'navegar'; to: string }
  | { tipo: 'host'; href: string }
  | { tipo: 'ok' };

/**
 * Cadeia de guardas na ordem do documento 14.3:
 * auth -> tenant -> app instalado -> permissão.
 *
 * Função pura de propósito: cada ramo aqui já custou um incidente (A1-A8,
 * B1-B9, C1-C5, V26/V43), e a única forma de garantir que o próximo ajuste
 * não desfaça o anterior é ter cada ramo num teste (`tests/route-guard.test.ts`)
 * que roda sem navegador. O componente abaixo só traduz o resultado em tela.
 *
 * Isto é experiência, não autorização. Quem decide de verdade é a RLS: mesmo
 * que um bug deixe a tela abrir, o banco recusa a consulta.
 */
export function desvioDaGuarda(e: EntradaDaGuarda): Desvio {
  if (e.status === 'loading') return { tipo: 'carregando' };
  if (e.status === 'erro') return { tipo: 'erro' };

  if (e.status === 'unauthed') {
    const next = encodeURIComponent(e.pathname + e.search);
    // Quem bate numa tela do cliente sem sessão volta para a porta DELE, com a
    // marca dele. Mandar para `/login` seria mostrar a tela da Yard a quem
    // estava tentando entrar na empresa onde trabalha.
    //
    // Em `{slug}.yardsuite.com.br` o subdomínio já É a porta - a raiz local
    // (`/`) é a tela de login dele, sem prefixo nenhum.
    if (e.slugDaUrl) {
      const raiz = e.emSubdominio ? '/' : `/${e.slugDaUrl}`;
      return { tipo: 'navegar', to: `${raiz}?next=${next}` };
    }
    return { tipo: 'navegar', to: `/login?next=${next}` };
  }

  // Autenticado mas sem organização: precisa criar ou aceitar convite.
  if (e.status === 'no-org') return { tipo: 'navegar', to: '/criar-organizacao' };

  // C3: admin de plataforma que TAMBÉM é membro de uma organização suspensa
  // (a policy `yard_ve_organizacoes` devolve a linha mesmo suspensa para a
  // equipe) ficava preso em `/suspended` - fora do Shell, sem menu, sem
  // saída pela tela. `/suspended` é aviso para o CLIENTE; a equipe sempre
  // tem o console.
  if (e.status === 'suspended' && !e.adminPlataforma) return { tipo: 'navegar', to: '/suspended' };

  // Administrador da Yard sem organização própria: a casa dele é o console.
  // Sem isto ele cairia no painel de uma organização que não existe. As
  // telas que não são de organização nenhuma (`ROTAS_SEM_TENANT`) passam.
  if (
    !e.orgSlug &&
    e.adminPlataforma &&
    !e.pathname.startsWith('/admin') &&
    !rotaSemTenant(e.pathname, e.slugDoCaminho)
  ) {
    return { tipo: 'navegar', to: '/admin' };
  }

  // Antes isto caía em /settings/apps, que virou tela da Yard: o cliente sem o
  // produto instalado ia parar num 403 em vez de voltar para casa.
  if (e.app && !e.installedApps.includes(e.app)) {
    return { tipo: 'navegar', to: e.orgSlug ? (comSlug('/home', e.slugAtivo) as string) : '/' };
  }

  // ── O endereço e a sessão têm de falar do mesmo cliente ─────────────────
  //
  // A tela lê os dados da organização ATIVA, não do slug. Se os dois discordam,
  // a barra de endereço mente: `/russirussi/bio` mostrando a Bio da CIMA. Já
  // aconteceu de a lista trazer os dois clientes juntos, e a correção disso
  // está em `useList`; aqui a garantia é a outra metade, a de que o endereço
  // nunca descreve uma conta que não é a que está aberta.
  //
  // Vínculo manda: quem é membro do cliente da URL passa a operar nele, que é
  // o que a pessoa pediu ao digitar aquele endereço ou abrir aquele favorito.
  // Quem não é volta para o próprio endereço, e nunca vê a tela do outro.
  if (e.slugDaUrl && e.orgSlug && e.slugDaUrl !== e.orgSlug) {
    // O pedido de troca já saiu pelo efeito do componente (A8) - aqui só
    // falta desenhar a espera.
    if (e.slugsDasOrgs.includes(e.slugDaUrl)) return { tipo: 'trocando' };
    // O slug da URL não é de organização nenhuma que esta pessoa tem. Em
    // `{slug}.yardsuite.com.br` o endereço certo é OUTRO subdomínio -
    // `Navigate` não troca de host, então é redirecionamento de verdade, não
    // navegação de rota.
    // Em máquina local (localhost, 127.0.0.1, *.localhost) o desvio fica no próprio
    // endereço local: nunca manda o teste para o domínio de produção.
    if (e.emSubdominio && !ehHostLocal()) return { tipo: 'host', href: `https://${e.orgSlug}.yardsuite.com.br/home` };
    return { tipo: 'navegar', to: `/${e.orgSlug}/home` };
  }

  if (e.soYard && !e.adminPlataforma) return { tipo: 'navegar', to: '/403' };

  if (e.permission && !e.can) return { tipo: 'navegar', to: '/403' };

  return { tipo: 'ok' };
}

export function RouteGuard({ app, permission, soYard, children }: Props) {
  const { status, installedApps, org, orgs, adminPlataforma, switchOrg } = useAuth();
  const can = useCan(permission);
  const location = useLocation();
  // O slug que está na barra de endereço - do caminho (`/cima/home`, modo
  // antigo/desenvolvimento) ou do subdomínio (`cima.yardsuite.com.br/home`).
  // Serve só para mandar de volta à porta certa: quem decide de qual
  // organização é a sessão continua sendo o banco.
  const { slug: slugDoCaminho } = useParams();
  const slugDaUrl = slugDoCaminho ?? slugDoSubdominio() ?? undefined;
  // Para PREFIXAR link/navegação (`comSlug`) - nulo em subdomínio de
  // cliente, onde prefixar de novo duplicaria o slug no caminho.
  const slugAtivo = useSlugDoCliente();
  // Guarda contra qual organização já foi pedida: sem isto, `switchOrg` seria
  // disparado de novo a cada re-render enquanto a troca está em andamento
  // (o `AuthProvider` re-renderiza a cada request do `hydrate`), competindo
  // consigo mesmo. Um pedido por alvo, e só.
  const trocaEmAndamentoPara = useRef<string | null>(null);

  /**
   * A8: o pedido de troca é efeito colateral, não conta de render. Chamar
   * `switchOrg` direto no corpo do componente rodava a cada render enquanto
   * o `ref` acima não travava (e sob `StrictMode` o render roda duas vezes de
   * propósito) - cada rota aninhada (`<G>`) monta o seu próprio `RouteGuard`,
   * cada um com o seu `ref`, competindo pelo mesmo pedido.
   *
   * Com A3 resolvido (trocar de organização pelo menu troca de HOST, não só
   * de URL), URL e sessão praticamente não divergem mais em produção - isto
   * vira rede de segurança de desenvolvimento: `localhost` não tem vhost
   * curinga, então `/cima/home` digitado à mão enquanto a sessão é de outra
   * organização ainda passa por aqui.
   */
  // Um pedido por endereço. Antes o `ref` era limpo no fim da troca; se o banco
  // não mudava a organização (visita do Console vencendo, migration 0153), o
  // efeito pedia de novo a cada renovação de token, sem parar. Agora só um slug
  // novo na URL libera outro pedido. Este efeito vem antes do de baixo de
  // propósito: os dois rodam na ordem em que estão escritos.
  useEffect(() => {
    trocaEmAndamentoPara.current = null;
  }, [slugDaUrl]);

  useEffect(() => {
    if (!slugDaUrl || !org || slugDaUrl === org.slug) return;
    const alvo = orgs.find((o) => o.slug === slugDaUrl);
    if (!alvo || trocaEmAndamentoPara.current === alvo.id) return;
    trocaEmAndamentoPara.current = alvo.id;
    void switchOrg(alvo.id);
  }, [slugDaUrl, org, orgs, switchOrg]);

  const desvio = desvioDaGuarda({
    status,
    adminPlataforma,
    orgSlug: org?.slug ?? null,
    slugsDasOrgs: orgs.map((o) => o.slug),
    slugDaUrl,
    slugDoCaminho,
    emSubdominio: !!slugDoSubdominio(),
    slugAtivo,
    pathname: location.pathname,
    search: location.search,
    installedApps,
    app,
    permission,
    can,
    soYard,
  });

  switch (desvio.tipo) {
    case 'carregando':
      return (
        <EsqueletoDaCasca forma={formaDaRota(location.pathname)} />
      );
    case 'erro':
      // Antes esta situação não existia como tela: a carga falhava, o estado
      // ficava em `loading` e a pessoa olhava "Carregando sua conta..." até
      // desistir e recarregar na mão. Agora ela vê o que houve e tem um botão.
      return (
        <div className="state">
          <h3>Não conseguimos carregar sua conta</h3>
          <p className="muted" style={{ maxWidth: '42ch', margin: 'var(--space-2) auto' }}>
            A conexão com o servidor falhou ou demorou demais. Seus dados estão a salvo; foi só esta
            tentativa que não completou.
          </p>
          <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
            Tentar de novo
          </button>
        </div>
      );
    case 'trocando':
      return (
        <EsqueletoDaCasca forma={formaDaRota(location.pathname)} />
      );
    case 'navegar':
      return <Navigate to={desvio.to} replace />;
    case 'host':
      window.location.href = desvio.href;
      return null;
    case 'ok':
      return <>{children}</>;
  }
}
